// ─── Notification Types ───
export enum NotificationType {
  LeaveRequest = 'leave_request',
  LeaveApproved = 'leave_approved',
  LeaveRejected = 'leave_rejected',
  MeetingMention = 'meeting_mention',
  Reaction = 'reaction',
  System = 'system',
  Comment = 'comment',
  Approval = 'approval',
  // Pending field-edit proposed by a non-admin user, awaiting admin decision.
  PendingChangeRequest = 'pending_change_request',
  // Approval/rejection of a pending change, sent back to the requester.
  PendingChangeDecision = 'pending_change_decision',
}

export interface NotificationVM {
  id: number;
  isRead: boolean;
  createdAt: string;
  message: string;
  senderAzureObjectId: string;
  azureObjectId: string;
  timeSheetEntryId: number;
  meetingNoteId: number;
  submittedFormId: number;
  rootMeetingNoteId: number;
  leaveRequestId?: number;
  globalRequestId?: number;
  // Owning company for the linked entity (SubmittedForm, LeaveRequest, etc.). Computed server-
  // side; used by the dropdown to surface the client name and trigger an org switch when the
  // notification belongs to a company other than the one currently managed on this tab.
  companyId?: number;

  // UI-derived fields (computed on frontend)
  type?: NotificationType;
  senderInitials?: string;
  senderName?: string;
  relativeTime?: string;
}

// ─── Helpers ───

/** Derive notification type from message content */
export function deriveNotificationType(note: NotificationVM): NotificationType {
  const msg = (note.message || '').toLowerCase();

  // Pending-change family — detect before leave/approval, since the decision message
  // contains "was approved/rejected" wording that would otherwise match LeaveApproved.
  if (msg.startsWith('your proposed change to')) return NotificationType.PendingChangeDecision;
  if (msg.includes('proposed a change to')) return NotificationType.PendingChangeRequest;

  if (msg.includes('has been approved')) return NotificationType.LeaveApproved;
  if (msg.includes('has been rejected')) return NotificationType.LeaveRejected;
  if (msg.includes('leave request') || msg.includes('requested') && (msg.includes('leave') || msg.includes('sick') || msg.includes('annual'))) return NotificationType.LeaveRequest;
  if (msg.includes('mentioned') && msg.includes('meeting note')) return NotificationType.MeetingMention;
  if (msg.includes('reacted')) return NotificationType.Reaction;
  if (msg.includes('comment') || msg.includes('replied')) return NotificationType.Comment;
  if (msg.includes('awaiting your approval') || msg.includes('awaiting approval')) return NotificationType.Approval;

  return NotificationType.System;
}

/** True when the notification refers to a pending field-edit change (request or decision). */
export function isPendingChangeNotification(note: NotificationVM): boolean {
  const t = note.type ?? deriveNotificationType(note);
  return t === NotificationType.PendingChangeRequest
      || t === NotificationType.PendingChangeDecision;
}

/** Get avatar config based on notification type */
export function getNotificationAvatar(type: NotificationType): { cssClass: string; icon: string; iconClass: string } {
  switch (type) {
    case NotificationType.LeaveRequest:
      return { cssClass: 'avatar-leave', icon: 'event_busy', iconClass: 'icon-leave' };
    case NotificationType.LeaveApproved:
      return { cssClass: 'avatar-approval', icon: 'check_circle', iconClass: 'icon-approval' };
    case NotificationType.LeaveRejected:
      return { cssClass: 'avatar-rejected', icon: 'cancel', iconClass: 'icon-rejected' };
    case NotificationType.MeetingMention:
      return { cssClass: 'avatar-meeting', icon: 'groups', iconClass: 'icon-meeting' };
    case NotificationType.Reaction:
      return { cssClass: 'avatar-reaction', icon: 'favorite', iconClass: 'icon-reaction' };
    case NotificationType.Comment:
      return { cssClass: 'avatar-comment', icon: 'chat_bubble', iconClass: 'icon-comment' };
    case NotificationType.Approval:
      return { cssClass: 'avatar-approval', icon: 'pending_actions', iconClass: 'icon-approval' };
    case NotificationType.PendingChangeRequest:
      return { cssClass: 'avatar-approval', icon: 'edit_note', iconClass: 'icon-approval' };
    case NotificationType.PendingChangeDecision:
      return { cssClass: 'avatar-approval', icon: 'rule', iconClass: 'icon-approval' };
    default:
      return { cssClass: 'avatar-system', icon: 'notifications', iconClass: 'icon-system' };
  }
}

/** Get status tag config */
export function getStatusTag(type: NotificationType): { label: string; cssClass: string } | null {
  switch (type) {
    case NotificationType.LeaveApproved:
      return { label: 'Approved', cssClass: 'tag-approved' };
    case NotificationType.LeaveRejected:
      return { label: 'Rejected', cssClass: 'tag-rejected' };
    case NotificationType.LeaveRequest:
    case NotificationType.Approval:
    case NotificationType.PendingChangeRequest:
      return { label: 'Pending', cssClass: 'tag-pending' };
    default:
      return null;
  }
}

/** Extract sender initials from message */
export function extractSenderInitials(message: string): string {
  const patterns = [
    /^([A-Z][a-z]+)\s+([A-Z][a-z]+)/,
    /^Leave for\s+([A-Z][a-z]+)\s+([A-Z][a-z]+)/,
    /^Leave request from\s+([A-Z][a-z]+)\s+([A-Z][a-z]+)/,
    /^New\s+\w+\s+leave request.*from\s+([A-Z][a-z]+)\s+([A-Z][a-z]+)/i,
    /^([A-Z][a-z]+)\s+([A-Z][a-z]+)\s+reacted/,
  ];

  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match) {
      return (match[1][0] + match[2][0]).toUpperCase();
    }
  }

  if (message.startsWith('Your ')) return 'ME';
  if (message.startsWith('You ')) return 'SY';

  return 'N';
}

/** Extract sender full name from message */
export function extractSenderName(message: string): string {
  const patterns = [
    /^([A-Z][a-z]+\s+[A-Z][a-z]+)/,
    /^Leave for\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/,
    /^Leave request from\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/,
    /from\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/,
  ];

  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match) return match[1];
  }

  return '';
}

/** Format relative time */
export function formatRelativeTime(dateString: string): string {
  if (!dateString) return '';

  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Enrich raw notification with UI fields */
export function enrichNotification(note: NotificationVM): NotificationVM {
  return {
    ...note,
    type: deriveNotificationType(note),
    senderInitials: extractSenderInitials(note.message),
    senderName: extractSenderName(note.message),
    relativeTime: formatRelativeTime(note.createdAt)
  };
}
